import { useCallback, useEffect, useState } from 'react';
import '../../assets/css/statistics.css';
import SuperAdminLayout from '../../components/superadmin/SuperAdminLayout';
import api from '../../config/axios-conf';
import { useToastContext } from '../../contexts/ToastContext';

function StatisticsContent() {
    const { showError } = useToastContext();
    const [summary, setSummary] = useState(null);
    const [topShops, setTopShops] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());

    const fetchStatistics = useCallback(async () => {
        try {
            setIsLoading(true);
            setError('');
            const token = localStorage.getItem('token');

            const [summaryRes, topShopsRes] = await Promise.all([
                api.get(`/statistics/system/summary?month=${month}&year=${year}`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                api.get(`/statistics/system/top-shops?month=${month}&year=${year}&limit=10`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            setSummary(summaryRes.data);
            const topShopsData = topShopsRes.data || [];
            const formattedTopShops = topShopsData.map((item) => {
                const dataValues = item.dataValues || item;
                return {
                    shopId: dataValues.shopId,
                    revenue: dataValues.revenue || dataValues.get?.('revenue') || 0,
                    profit: dataValues.profit || dataValues.get?.('profit') || 0,
                    orderCount: dataValues.orderCount || dataValues.get?.('orderCount') || 0,
                    shop: item.shop || item.Shop || dataValues.shop || dataValues.Shop,
                };
            });
            setTopShops(formattedTopShops);
        } catch (err) {
            const errorMsg = err.response?.data?.message || 'Không thể tải dữ liệu thống kê';
            setError(errorMsg);
            showError(errorMsg);
        } finally {
            setIsLoading(false);
        }
    }, [month, year, showError]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    if (isLoading) {
        return (
            <div className="statistics">
                <div className="statistics__loading">Đang tải...</div>
            </div>
        );
    }

    return (
        <div className="statistics">
            <div className="statistics__header">
                <h1 className="statistics__title">Thống kê tổng hợp hệ thống</h1>
                <div className="statistics__filter">
                    <label className="statistics__filter-label">
                        Tháng:
                        <select value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="statistics__select">
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                                <option key={m} value={m}>Tháng {m}</option>
                            ))}
                        </select>
                    </label>
                    <label className="statistics__filter-label">
                        Năm:
                        <input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="statistics__input" min="2020" max="2100" />
                    </label>
                </div>
            </div>

            {error && <div className="statistics__error">{error}</div>}

            {summary && (
                <div className="statistics__grid">
                    <div className="statistics__card">
                        <h3 className="statistics__card-title">Tổng hợp tháng {month}/{year}</h3>
                        <div className="statistics__stats">
                            <div className="statistics__stat">
                                <div className="statistics__stat-label">Tổng doanh thu</div>
                                <div className="statistics__stat-value statistics__stat-value--large">
                                    {parseFloat(summary.totalRevenue || 0).toLocaleString('vi-VN')} đ
                                </div>
                            </div>
                            <div className="statistics__stat">
                                <div className="statistics__stat-label">Tổng lợi nhuận</div>
                                <div className="statistics__stat-value statistics__stat-value--large">
                                    {parseFloat(summary.totalProfit || 0).toLocaleString('vi-VN')} đ
                                </div>
                            </div>
                            <div className="statistics__stat">
                                <div className="statistics__stat-label">Tổng số đơn hàng</div>
                                <div className="statistics__stat-value">{summary.totalOrders || 0}</div>
                            </div>
                            <div className="statistics__stat">
                                <div className="statistics__stat-label">Khách hàng mới</div>
                                <div className="statistics__stat-value">{summary.newCustomers || 0}</div>
                            </div>
                            <div className="statistics__stat">
                                <div className="statistics__stat-label">Giá trị đơn trung bình</div>
                                <div className="statistics__stat-value">
                                    {parseFloat(summary.averageOrderValue || 0).toLocaleString('vi-VN')} đ
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {topShops && topShops.length > 0 && (
                <div className="statistics__card">
                    <h3 className="statistics__card-title">Top 10 Shops theo doanh thu</h3>
                    <div className="statistics__table-container">
                        <table className="statistics__table">
                            <thead>
                                <tr>
                                    <th>Thứ hạng</th>
                                    <th>Tên Shop</th>
                                    <th>Code</th>
                                    <th>Doanh thu</th>
                                    <th>Lợi nhuận</th>
                                    <th>Số đơn</th>
                                    <th>Trạng thái</th>
                                </tr>
                            </thead>
                            <tbody>
                                {topShops.map((item, index) => {
                                    const revenue = parseFloat(item.revenue || 0);
                                    const profit = parseFloat(item.profit || 0);
                                    const orderCount = parseInt(item.orderCount || 0);
                                    const shop = item.shop;
                                    return (
                                        <tr key={item.shopId || index}>
                                            <td>{index + 1}</td>
                                            <td>{shop?.nameShop || 'N/A'}</td>
                                            <td>{shop?.code || 'N/A'}</td>
                                            <td>{revenue.toLocaleString('vi-VN')} đ</td>
                                            <td>{profit.toLocaleString('vi-VN')} đ</td>
                                            <td>{orderCount}</td>
                                            <td>
                                                <span className={shop?.isActive ? 'statistics__badge statistics__badge--success' : 'statistics__badge statistics__badge--danger'}>
                                                    {shop?.isActive ? 'Hoạt động' : 'Tạm khóa'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function Statistics() {
    return (
        <SuperAdminLayout>
            <StatisticsContent />
        </SuperAdminLayout>
    );
}
